// react
import { useContext, useEffect, useMemo, useRef } from 'react';

// context
import { ContextSSE, ContextSSEProps, SSENotification } from './SSE.context';

// Re-export types for backward compatibility
export type {
	SSEConnectionStatus,
	SSENotification,
	SSENotificationPayload,
	SSENotificationType,
} from './SSE.context';

export interface UseSSEParams {
	/**
	 * Optional filter function to filter notifications.
	 * Only notifications that pass the filter will be included in the returned notifications array
	 * and trigger the onNotification callback.
	 */
	filter?: (notification: SSENotification) => boolean;
	/**
	 * Callback when a notification is received.
	 * This allows components to react to notifications without managing connection state.
	 */
	onNotification?: (notification: SSENotification) => void;
}

export interface UseSSEReturn extends Omit<ContextSSEProps, 'notifications'> {
	notifications: SSENotification[];
}

/**
 * Hook to access the shared SSE connection with optional notification callback and filter.
 *
 * This hook connects to the SSE context managed by SSEProvider.
 * The connection configuration (url, userId, autoConnect) is handled at the provider level.
 *
 * @example
 * ```tsx
 * // Simple usage - just access connection state
 * const { isConnected, notifications, status } = useSSE();
 *
 * // With notification callback
 * const { isConnected, notifications } = useSSE({
 *   onNotification: (notification) => {
 *     console.log('New notification:', notification);
 *   },
 * });
 *
 * // With filter - only receive notifications of a specific type
 * const { notifications } = useSSE({
 *   filter: (notification) => notification.type === 'docAI',
 *   onNotification: (notification) => {
 *     console.log('Transaction update:', notification);
 *   },
 * });
 * ```
 *
 * @param params - Optional configuration
 * @param params.filter - Optional filter function to filter notifications
 * @param params.onNotification - Callback fired when a new notification arrives (after filtering)
 * @returns SSE connection state and methods from context
 */

export const useSSE = ({ filter, onNotification }: UseSSEParams = {}): UseSSEReturn => {
	const context = useContext(ContextSSE);
	const refFilter = useRef(filter);
	const refOnNotification = useRef(onNotification);

	// update refs when callbacks change
	useEffect(() => {
		refFilter.current = filter;
		refOnNotification.current = onNotification;
	}, [filter, onNotification]);

	// filter notifications
	const filteredNotifications = useMemo(() => {
		if (!filter) {
			return context.notifications;
		}
		return context.notifications.filter(filter);
	}, [context.notifications, filter]);

	// subscribe to notifications
	useEffect(() => {
		if (!refOnNotification.current || !context.notifications.length) {
			return;
		}

		// get the latest notification
		const latestNotification = context.notifications[0];

		// check if it passes the filter
		if (latestNotification && (!refFilter.current || refFilter.current(latestNotification))) {
			refOnNotification.current(latestNotification);
		}
		// only fire when notifications array changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [context.notifications]);

	if (!context) {
		console.warn(
			'[useSSE] SSEProvider not found in component tree. Wrap your app with SSEProvider.'
		);
	}

	return {
		...context,
		notifications: filteredNotifications,
	};
};
